import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stepper,
    Step,
    StepLabel,
    Alert,
    Chip,
    IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';


import EmailIcon from '@mui/icons-material/Email';

const BookingPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    
    // Get context from URL params
    const from = searchParams.get('from') || 'direct';
    const tripId = searchParams.get('tripId');
    const dealId = searchParams.get('dealId');
    const promo = searchParams.get('promo');

    // Form state
    const [formData, setFormData] = useState({
        destination: searchParams.get('destination') || '',
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        adults: 1,
        children: 0,
        email: '',
        phone: '',
        specialRequests: '',
    });

    const [submitted, setSubmitted] = useState(false);

    const steps = ['Trip Details', 'Contact Info', 'Confirm'];

    const handleChange = (field: string) => (event: any) => {
        setFormData({ ...formData, [field]: event.target.value });
    };

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleSubmit = () => {
        // TODO: Submit booking to backend
        console.log('Booking submitted:', { ...formData, from, tripId, dealId, promo });
        setSubmitted(true);
    };

    const getSourceMessage = () => {
        switch (from) {
            case 'community':
                return 'Inspired by a community trip';
            case 'destination':
                return 'Partner deal selected';
            case 'mytrips':
                return 'Based on your preferences';
            default:
                return 'Plan your perfect trip';
        }
    };

    if (submitted) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
                <Container maxWidth="sm">
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '16px' }}>
                        <Typography variant="h4" sx={{ color: '#2D6E6A', mb: 2 }}>
                            🎉 Booking Confirmed!
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            We've sent a confirmation to <strong>{formData.email}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            You'll receive trip reminders at 2 weeks, 1 week, and 1 day before your trip.
                        </Typography>
                        <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                            <strong>Want to manage your trip?</strong><br />
                            Create an account to access your full itinerary, make changes, and earn credits!
                        </Alert>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/signup')}
                                sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
                            >
                                Create Account
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/community')}
                                sx={{ borderColor: '#2D6E6A', color: '#2D6E6A' }}
                            >
                                Browse More Trips
                            </Button>
                        </Box>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#2D6E6A', color: '#fff', py: 3 }}>
                <Container maxWidth="md">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: '#fff' }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                Book a Trip
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {getSourceMessage()}
                            </Typography>
                        </Box>
                    </Box>
                    {promo && (
                        <Chip
                            label={`Promo: ${promo} applied!`}
                            sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                        />
                    )}
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: 4 }}>
                {/* Stepper */}
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Paper sx={{ p: 4, borderRadius: '16px' }}>
                    {/* Step 1: Trip Details */}
                    {activeStep === 0 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FlightTakeoffIcon sx={{ color: '#2D6E6A' }} />
                                Where and When?
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Destination"
                                        placeholder="e.g., Paris, France"
                                        value={formData.destination}
                                        onChange={handleChange('destination')}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Start Date"
                                        type="date"
                                        value={formData.startDate}
                                        onChange={handleChange('startDate')}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="End Date"
                                        type="date"
                                        value={formData.endDate}
                                        onChange={handleChange('endDate')}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Adults</InputLabel>
                                        <Select
                                            value={formData.adults}
                                            label="Adults"
                                            onChange={handleChange('adults')}
                                        >
                                            {[1, 2, 3, 4, 5, 6].map((n) => (
                                                <MenuItem key={n} value={n}>{n}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Children</InputLabel>
                                        <Select
                                            value={formData.children}
                                            label="Children"
                                            onChange={handleChange('children')}
                                        >
                                            {[0, 1, 2, 3, 4].map((n) => (
                                                <MenuItem key={n} value={n}>{n}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Step 2: Contact Info */}
                    {activeStep === 1 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon sx={{ color: '#2D6E6A' }} />
                                How can we reach you?
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        placeholder="your@email.com"
                                        value={formData.email}
                                        onChange={handleChange('email')}
                                        required
                                        helperText="We'll send your booking confirmation here"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Phone (optional)"
                                        type="tel"
                                        placeholder="+1 (555) 123-4567"
                                        value={formData.phone}
                                        onChange={handleChange('phone')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Special Requests (optional)"
                                        multiline
                                        rows={3}
                                        placeholder="Any special requirements or preferences..."
                                        value={formData.specialRequests}
                                        onChange={handleChange('specialRequests')}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Step 3: Confirm */}
                    {activeStep === 2 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 3 }}>
                                Confirm Your Booking
                            </Typography>
                            <Paper sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: '12px', mb: 3 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Destination</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            📍 {formData.destination || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">Dates</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            📅 {formData.startDate || '?'} → {formData.endDate || '?'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">Travelers</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            👥 {formData.adults} Adult{formData.adults > 1 ? 's' : ''}{formData.children > 0 ? `, ${formData.children} Child${formData.children > 1 ? 'ren' : ''}` : ''}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Contact</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            ✉️ {formData.email}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                            <Alert severity="info">
                                You'll receive trip reminders 2 weeks, 1 week, and 1 day before your trip.
                            </Alert>
                        </Box>
                    )}

                    {/* Navigation Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            sx={{ color: '#666' }}
                        >
                            Back
                        </Button>
                        {activeStep < steps.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
                            >
                                Continue
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#009688' } }}
                            >
                                Confirm Booking
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default BookingPage;


